import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getOrdini, createOrdine, updateOrdineStato, deleteOrdine, getFornitori, getArticoli } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, ShoppingCart, MessageCircle, ExternalLink, Package, Lock, X, Search, Send, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

const STATI = [
  { value: 'creato', label: 'Creato', color: 'bg-slate-500' },
  { value: 'inviato', label: 'Inviato', color: 'bg-blue-500' },
  { value: 'confermato', label: 'Confermato', color: 'bg-green-500' },
  { value: 'ricevuto', label: 'Ricevuto', color: 'bg-primary' },
  { value: 'annullato', label: 'Annullato', color: 'bg-destructive' },
];

const Ordini = () => {
  const [ordini, setOrdini] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [articoli, setArticoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewOrdine, setViewOrdine] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Form state - nuovo flusso basato su articoli
  const [selectedItems, setSelectedItems] = useState({}); // {articoloId: quantity}
  const [searchQuery, setSearchQuery] = useState('');
  const [showRiepilogo, setShowRiepilogo] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ordiniData, fornitoriData, articoliData] = await Promise.all([
        getOrdini(),
        getFornitori(),
        getArticoli()
      ]);
      setOrdini(ordiniData);
      setFornitori(fornitoriData);
      setArticoli(articoliData);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedItems({});
    setSearchQuery('');
    setShowRiepilogo(false);
    setNote('');
  };

  // Filtra articoli in base alla ricerca
  const filteredArticoli = articoli.filter(a => 
    searchQuery === '' || 
    a.nome.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Raggruppa articoli selezionati per fornitore
  const getOrdiniPerFornitore = () => {
    const ordiniMap = {};
    
    Object.entries(selectedItems).forEach(([articoloId, qty]) => {
      const articolo = articoli.find(a => a.id === articoloId);
      if (!articolo) return;
      
      const fornitoreId = articolo.fornitore_id || 'senza_fornitore';
      const fornitore = fornitori.find(f => f.id === fornitoreId);
      
      if (!ordiniMap[fornitoreId]) {
        ordiniMap[fornitoreId] = {
          fornitore_id: fornitoreId,
          fornitore_nome: fornitore?.nome || 'Senza Fornitore',
          fornitore_telefono: fornitore?.telefono || '',
          items: [],
          totale: 0
        };
      }
      
      const subtotale = (articolo.prezzo_acquisto || 0) * qty;
      ordiniMap[fornitoreId].items.push({
        articolo_id: articoloId,
        articolo_nome: articolo.nome,
        quantita: qty,
        prezzo_unitario: articolo.prezzo_acquisto || 0,
        unita: articolo.unita_misura || articolo.unita || 'pz',
        subtotale
      });
      ordiniMap[fornitoreId].totale += subtotale;
    });
    
    return Object.values(ordiniMap).filter(o => o.fornitore_id !== 'senza_fornitore');
  };

  const ordiniDaCreare = getOrdiniPerFornitore();
  const articoliSenzaFornitore = Object.entries(selectedItems)
    .map(([id, qty]) => ({ ...articoli.find(a => a.id === id), qty }))
    .filter(a => !a.fornitore_id);

  const handleQuantityChange = (articoloId, quantity) => {
    if (quantity <= 0) {
      const newItems = { ...selectedItems };
      delete newItems[articoloId];
      setSelectedItems(newItems);
    } else {
      setSelectedItems({
        ...selectedItems,
        [articoloId]: quantity
      });
    }
  };

  const addArticolo = (articolo) => {
    setSelectedItems({
      ...selectedItems,
      [articolo.id]: 1
    });
    setSearchQuery('');
  };

  const removeArticolo = (articoloId) => {
    const newItems = { ...selectedItems };
    delete newItems[articoloId];
    setSelectedItems(newItems);
  };

  const handleProcediRiepilogo = () => {
    if (Object.keys(selectedItems).length === 0) {
      toast.error('Seleziona almeno un articolo');
      return;
    }
    
    if (articoliSenzaFornitore.length > 0) {
      toast.warning(`${articoliSenzaFornitore.length} articoli non hanno un fornitore assegnato e verranno esclusi`);
    }
    
    if (ordiniDaCreare.length === 0) {
      toast.error('Nessun articolo con fornitore assegnato');
      return;
    }
    
    setShowRiepilogo(true);
  };

  const handleCreaOrdini = async () => {
    setSaving(true);
    const ordiniCreati = [];
    const errori = [];
    
    for (const ordine of ordiniDaCreare) {
      try {
        const newOrdine = await createOrdine({
          fornitore_id: ordine.fornitore_id,
          items: ordine.items.map(item => ({
            articolo_id: item.articolo_id,
            articolo_nome: item.articolo_nome,
            quantita: item.quantita,
            prezzo_unitario: item.prezzo_unitario,
            unita: item.unita
          })),
          note: note || null
        });
        ordiniCreati.push(newOrdine);
      } catch (error) {
        errori.push(ordine.fornitore_nome);
      }
    }
    
    setSaving(false);
    
    if (ordiniCreati.length > 0) {
      toast.success(`Creati ${ordiniCreati.length} ordini!`);
      setShowForm(false);
      resetForm();
      loadData();
    }
    
    if (errori.length > 0) {
      toast.error(`Errore per: ${errori.join(', ')}`);
    }
  };

  const handleSendWhatsApp = async (ordine) => {
    window.open(ordine.whatsapp_link, '_blank');
    
    if (ordine.stato === 'creato') {
      try {
        await updateOrdineStato(ordine.id, 'inviato');
        toast.success('Stato aggiornato a "Inviato"');
        loadData();
        if (viewOrdine?.id === ordine.id) {
          setViewOrdine({ ...ordine, stato: 'inviato' });
        }
      } catch (error) {
        console.error('Errore aggiornamento stato:', error);
      }
    }
  };

  const handleUpdateStato = async (id, stato, ordine) => {
    if (!isAdmin && !ordine.is_owner) {
      toast.error('Non puoi modificare gli ordini di altri');
      return;
    }
    try {
      await updateOrdineStato(id, stato);
      toast.success('Stato aggiornato!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id, ordine) => {
    if (!isAdmin && !ordine.is_owner) {
      toast.error('Non puoi eliminare gli ordini di altri');
      return;
    }
    if (!window.confirm('Sei sicuro di voler eliminare questo ordine?')) return;
    try {
      await deleteOrdine(id);
      toast.success('Ordine eliminato!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const getStatoBadge = (stato) => {
    const statoInfo = STATI.find(s => s.value === stato) || STATI[0];
    return (
      <Badge className={`${statoInfo.color} text-white`}>
        {statoInfo.label}
      </Badge>
    );
  };

  const getFornitoreNome = (articoloId) => {
    const articolo = articoli.find(a => a.id === articoloId);
    if (!articolo?.fornitore_id) return null;
    const fornitore = fornitori.find(f => f.id === articolo.fornitore_id);
    return fornitore?.nome;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ordini-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Ordini</h1>
          <p className="text-muted-foreground mt-1">Cerca articoli e crea ordini automatici per fornitore</p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} data-testid="add-ordine-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Ordine
          </Button>
        )}
      </div>

      {/* Form Nuovo Ordine */}
      {showForm && !showRiepilogo && (
        <Card className="border-primary/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Seleziona Articoli</h2>
              <Button variant="ghost" size="icon" onClick={() => { setShowForm(false); resetForm(); }}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Carrello */}
            {Object.keys(selectedItems).length > 0 && (
              <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-primary" />
                    Carrello ({Object.keys(selectedItems).length} articoli)
                  </Label>
                  <Badge variant="outline">{ordiniDaCreare.length} fornitori</Badge>
                </div>
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {Object.entries(selectedItems).map(([articoloId, qty]) => {
                    const articolo = articoli.find(a => a.id === articoloId);
                    const fornitoreNome = getFornitoreNome(articoloId);
                    return (
                      <div key={articoloId} className="flex items-center gap-2 p-2 bg-background rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{articolo?.nome}</p>
                          <p className="text-xs text-muted-foreground">
                            {fornitoreNome ? (
                              <span className="text-primary">{fornitoreNome}</span>
                            ) : (
                              <span className="text-destructive">⚠️ Senza fornitore</span>
                            )}
                          </p>
                        </div>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min="1"
                          value={qty}
                          onChange={(e) => handleQuantityChange(articoloId, parseInt(e.target.value) || 0)}
                          className="w-16 h-9 text-center"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-destructive hover:text-destructive"
                          onClick={() => removeArticolo(articoloId)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    {articoliSenzaFornitore.length > 0 && (
                      <span className="text-destructive">
                        ⚠️ {articoliSenzaFornitore.length} senza fornitore
                      </span>
                    )}
                  </span>
                  <Button onClick={handleProcediRiepilogo}>
                    Procedi
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* Ricerca Articoli */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Cerca articolo..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="search-articolo"
                />
              </div>
              
              {/* Lista Articoli */}
              <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                {filteredArticoli.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4 text-center">
                    {searchQuery ? 'Nessun articolo trovato' : 'Nessun articolo disponibile'}
                  </p>
                ) : (
                  filteredArticoli
                    .filter(a => !selectedItems[a.id])
                    .slice(0, searchQuery ? 50 : 20)
                    .map((articolo) => {
                      const fornitore = fornitori.find(f => f.id === articolo.fornitore_id);
                      return (
                        <div 
                          key={articolo.id} 
                          className="flex items-center justify-between p-3 hover:bg-secondary/50 cursor-pointer border-b last:border-b-0"
                          onClick={() => addArticolo(articolo)}
                          data-testid={`add-articolo-${articolo.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{articolo.nome}</p>
                            <p className="text-sm text-muted-foreground">
                              {fornitore ? (
                                <span className="text-primary">{fornitore.nome}</span>
                              ) : (
                                <span className="text-destructive">Senza fornitore</span>
                              )}
                              {articolo.prezzo_acquisto > 0 && (
                                <span className="ml-2 font-mono">€{articolo.prezzo_acquisto.toFixed(2)}</span>
                              )}
                            </p>
                          </div>
                          <Plus className="h-5 w-5 text-primary flex-shrink-0" />
                        </div>
                      );
                    })
                )}
                {!searchQuery && filteredArticoli.filter(a => !selectedItems[a.id]).length > 20 && (
                  <p className="text-xs text-muted-foreground p-3 text-center bg-secondary/30">
                    Usa la ricerca per trovare altri articoli...
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Riepilogo Ordini per Fornitore */}
      {showForm && showRiepilogo && (
        <Card className="border-primary/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Riepilogo Ordini</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowRiepilogo(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <p className="text-muted-foreground mb-4">
              Verranno creati <strong>{ordiniDaCreare.length} ordini</strong> separati per i seguenti fornitori:
            </p>

            <div className="space-y-4 mb-6">
              {ordiniDaCreare.map((ordine, idx) => (
                <Card key={idx} className="bg-secondary/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span>{ordine.fornitore_nome}</span>
                      <span className="font-mono text-primary">€{ordine.totale.toFixed(2)}</span>
                    </CardTitle>
                    {ordine.fornitore_telefono && (
                      <p className="text-sm text-muted-foreground">{ordine.fornitore_telefono}</p>
                    )}
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="divide-y">
                      {ordine.items.map((item, i) => (
                        <div key={i} className="flex justify-between py-2 text-sm">
                          <span>{item.quantita}x {item.articolo_nome}</span>
                          <span className="font-mono text-muted-foreground">
                            €{item.subtotale.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="note">Note per tutti gli ordini (opzionale)</Label>
                <Textarea
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Note aggiuntive..."
                  rows={2}
                />
              </div>

              <div className="flex justify-between gap-2">
                <Button variant="outline" onClick={() => setShowRiepilogo(false)}>
                  Indietro
                </Button>
                <Button onClick={handleCreaOrdini} disabled={saving}>
                  {saving ? 'Creazione...' : `Crea ${ordiniDaCreare.length} Ordini`}
                  <Send className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Ordine Dialog */}
      <Dialog open={!!viewOrdine} onOpenChange={() => setViewOrdine(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Dettaglio Ordine</DialogTitle>
            <DialogDescription>Riepilogo dell'ordine e invio WhatsApp</DialogDescription>
          </DialogHeader>
          {viewOrdine && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-lg">{viewOrdine.fornitore_nome}</p>
                  <p className="text-sm text-muted-foreground font-mono">{viewOrdine.fornitore_telefono}</p>
                </div>
                {getStatoBadge(viewOrdine.stato)}
              </div>
              
              <div className="border rounded-lg divide-y">
                {viewOrdine.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between p-3">
                    <div>
                      <p className="font-medium">{item.articolo_nome}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.quantita} x €{(item.prezzo_unitario || 0).toFixed(2)}
                      </p>
                    </div>
                    <p className="font-mono font-medium">
                      €{(item.quantita * (item.prezzo_unitario || 0)).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-semibold">Totale</span>
                <span className="text-xl font-bold font-mono text-primary">
                  €{viewOrdine.totale.toFixed(2)}
                </span>
              </div>

              <Button
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => handleSendWhatsApp(viewOrdine)}
                data-testid="send-whatsapp"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Invia su WhatsApp
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lista Ordini */}
      {!showForm && (
        <>
          {ordini.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nessun ordine creato</p>
                <p className="text-sm text-muted-foreground">Clicca su "Nuovo Ordine" per iniziare</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {ordini.map((ordine) => (
                <Card key={ordine.id} className="hover:border-primary/30 transition-colors" data-testid={`ordine-card-${ordine.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{ordine.fornitore_nome}</h3>
                          {getStatoBadge(ordine.stato)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {ordine.items.length} articoli • {format(new Date(ordine.created_at), 'dd/MM/yyyy HH:mm')}
                          {ordine.user_nome && (
                            <span className="ml-2">• Creato da <strong>{ordine.user_nome}</strong></span>
                          )}
                        </p>
                        {ordine.stato_cambiato_da_nome && ordine.stato_cambiato_at && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Stato cambiato da <strong>{ordine.stato_cambiato_da_nome}</strong> il {format(new Date(ordine.stato_cambiato_at), 'dd/MM HH:mm')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-xl font-bold font-mono text-primary">
                          €{ordine.totale.toFixed(2)}
                        </p>
                        <div className="flex gap-2">
                          {(isAdmin || ordine.is_owner) ? (
                            <Select
                              value={ordine.stato}
                              onValueChange={(value) => handleUpdateStato(ordine.id, value, ordine)}
                            >
                              <SelectTrigger className="w-[130px]" data-testid={`stato-ordine-${ordine.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATI.map((s) => (
                                  <SelectItem key={s.value} value={s.value}>
                                    {s.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="outline" className="h-10 px-3 flex items-center gap-1">
                              <Lock className="h-3 w-3" />
                              {STATI.find(s => s.value === ordine.stato)?.label}
                            </Badge>
                          )}
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setViewOrdine(ordine)}
                            data-testid={`view-ordine-${ordine.id}`}
                          >
                            <Package className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="bg-green-600/10 border-green-600/50 hover:bg-green-600/20"
                            onClick={() => handleSendWhatsApp(ordine)}
                            data-testid={`whatsapp-ordine-${ordine.id}`}
                          >
                            <MessageCircle className="h-4 w-4 text-green-500" />
                          </Button>
                          {(isAdmin || ordine.is_owner) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDelete(ordine.id, ordine)}
                              data-testid={`delete-ordine-${ordine.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Ordini;
